import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { sortTiers } from '../../../../helpers/tiers';
import { fetchTiers } from '../../../../actions/tierActions';
import { fetchSubscriptionTier, changeSubscriptionTier } from '../../../../actions/profileActions';
import { analyticsPush } from '../../../../actions/applicationActions';
import ChangeTier from './ChangeTier';
import ChangeTierSavod from './ChangeTierSavod';
import { useQuery } from '@apollo/client';
import querySubscription from '../../../../graphql/querySubscription.graphql';
import { getSubscriptionClient } from '../../../../api/subscriptionService';
import { stateRender } from '../../../../helpers/react';
import { OperationNames } from '../../../../constants/OtelSpans';
import { OperationTelemetry } from '@rtl_nl/rtl-otel-js-web';
import { getExperiment } from '../../../../helpers/sitespect';
import { SITESPECT_EXPERIMENT_NAMES, SITESPECT_EXPERIMENT } from '../../../../constants/Sitespect';

const isPending = ({ tiers, subscription: { tier, changeTier } }) =>
	tiers.pending || tier.pending || changeTier.pending;
const isError = ({ tiers, subscription: { tier, changeTier } }) =>
	tiers.error || tier.error || changeTier.error;

const ChangeTierContainer = () => {
	const changeSubscriptionTierTelemetry = new OperationTelemetry(OperationNames.CHANGE_TIER);
	const history = useHistory();
	const rawTierCollection = useSelector((state) => state.tiers.collection || []);
	const rawSavodCollection = useSelector((state) => state.tiers.savodCollection || []);

	const tierCollection = useMemo(() => {
		const sorted = sortTiers(rawTierCollection);
		return [...sorted].reverse();
	}, [rawTierCollection]);

	const savodCollection = useMemo(() => sortTiers(rawSavodCollection), [rawSavodCollection]);
	const isTiersPending = useSelector((state) => isPending(state));
	const isTiersError = useSelector((state) => isError(state));
	const currentTier = useSelector((state) => state.subscription.tier.data);

	const savodChangeTierExperiment = getExperiment(
		SITESPECT_EXPERIMENT_NAMES.SAVOD_CHANGE_TIER_EXPERIMENT
	);

	const dispatch = useDispatch();
	const dispatchChangeSubscriptionTier = (tierId) =>
		dispatch(changeSubscriptionTier({ tierId, changeSubscriptionTierTelemetry }));
	const dispatchAnalyticsPush = (options, payload) => dispatch(analyticsPush(options, payload));
	const subscriptionClient = getSubscriptionClient();
	const { data, loading } = useQuery(querySubscription, {
		client: subscriptionClient,
	});

	const subscription = data?.me?.subscription;

	useEffect(() => {
		dispatch(fetchTiers());
		if (!currentTier) {
			dispatch(fetchSubscriptionTier(changeSubscriptionTierTelemetry));
		}
	}, [currentTier, dispatch]);

	if (!currentTier) return null;

	return stateRender({
		isPending: loading || isTiersPending || !tierCollection.length || savodCollection.length === 0,
		onSuccess: () => (
			<>
				{savodChangeTierExperiment === SITESPECT_EXPERIMENT.VARIANT ? (
					<ChangeTierSavod
						history={history}
						tierCollection={tierCollection}
						savodCollection={savodCollection}
						isTiersPending={isTiersPending}
						isTiersError={isTiersError}
						changeSubscriptionTier={dispatchChangeSubscriptionTier}
						currentTier={currentTier}
						analyticsPush={dispatchAnalyticsPush}
						subscription={subscription}
					/>
				) : (
					<ChangeTier
						savodCollection={savodCollection}
						history={history}
						tierCollection={tierCollection}
						isTiersPending={isTiersPending}
						isTiersError={isTiersError}
						changeSubscriptionTier={dispatchChangeSubscriptionTier}
						currentTier={currentTier}
						analyticsPush={dispatchAnalyticsPush}
						subscription={subscription}
					/>
				)}
			</>
		),
	});
};

export default ChangeTierContainer;
